import { useCallback, useEffect, useState } from 'react';
import { useTokens } from '../theme.jsx';
import { notifyError, notifySuccess } from '../toast.js';
import {
  approvePermissionRequest,
  listPermissionRequests,
  rejectPermissionRequest,
} from '../services/institutions.js';
import { Button, Card, SectionTitle } from '../uiKit.jsx';
import { RowsSkeleton } from '../Skeleton.jsx';

const SCOPE_LABELS = {
  ANALYTICS_VIEW: 'Analytics',
  SEATS_MANAGE: 'Seat management',
  BRANDING_MANAGE: 'Branding',
  ADMINS_MANAGE: 'Team and audit log',
  BILLING_VIEW: 'Billing and invoices',
};

export default function PermissionRequestsPanel({ institutionId }) {
  const t = useTokens();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPermissionRequests(institutionId);
      setRequests(data.requests || []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (request, approved) => {
    setBusyId(request.id);
    try {
      if (approved) {
        await approvePermissionRequest(request.id, institutionId);
      } else {
        await rejectPermissionRequest(request.id, institutionId);
      }
      notifySuccess(approved ? 'Permission granted.' : 'Permission request declined.');
      await load();
    } catch (error) {
      notifyError(error?.response?.data?.error || 'Could not update the permission request.');
    } finally {
      setBusyId(null);
    }
  };

  if (!loading && requests.length === 0) { return null; }

  return (
    <Card>
      <SectionTitle>Permission requests</SectionTitle>
      {loading ? <RowsSkeleton rows={2} /> : requests.map((request) => {
        return (
          <div key={request.id} className="border-t py-4 first:border-t-0 first:pt-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: t.hairline }}>
            <div>
              <p className="text-sm font-semibold" style={{ color: t.heading }}>{request.requester?.email || 'Administrator'}</p>
              <p className="mt-1 text-sm" style={{ color: t.muted }}>
                Requests {SCOPE_LABELS[request.requestedScope] || request.requestedScope}
              </p>
              {request.reason ? <p className="mt-1 text-sm" style={{ color: t.body }}>{request.reason}</p> : null}
            </div>
            <div className="flex gap-2">
              <Button small variant="secondary" label="Decline" disabled={busyId === request.id} onClick={() => { decide(request, false); }} />
              <Button small label={busyId === request.id ? 'Saving…' : 'Approve'} disabled={busyId === request.id} onClick={() => { decide(request, true); }} />
            </div>
          </div>
        );
      })}
    </Card>
  );
}
